import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { WindowSize } from 'hooks/useResize';
import { logError } from 'setup/logger/logError';

import { CanvasDrawElement } from '../types';

interface UseCanvasControlsParams {
  initialDrawing: string;
  onTogglePainting: () => void;
  onClear: () => void;
  src?: string;
  windowSize: WindowSize;
  paintCanvasWidth: number;
  paintCanvasHeight: number;
  drawModeOff: () => void;
}

interface UseCanvasControlsResult {
  canvasDrawElementRef: MutableRefObject<CanvasDrawElement | null>;
  setCanvasRef: (instance: CanvasDrawElement | null) => void;
  stepBack: () => void;
  reset: () => void;
  getData: () => string;
  toggleDrawing: () => void;
}

export default function useCanvasControls({
  initialDrawing,
  onTogglePainting,
  onClear,
  src,
  windowSize,
  paintCanvasWidth,
  paintCanvasHeight,
  drawModeOff,
}: UseCanvasControlsParams): UseCanvasControlsResult {
  const canvasDrawElementRef = useRef<CanvasDrawElement | null>(null);
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  const [data, setData] = useState(initialDrawing);

  const setCanvasRef = useCallback((instance: CanvasDrawElement | null) => {
    canvasDrawElementRef.current = instance;
    setIsCanvasReady(Boolean(instance));
  }, []);

  const stepBack = useCallback(() => {
    canvasDrawElementRef.current?.undo();
  }, []);

  const reset = useCallback(() => {
    canvasDrawElementRef.current?.clear();
    onClear();
  }, [onClear]);

  const getData = useCallback(() => {
    return canvasDrawElementRef.current?.getSaveData() ?? '';
  }, []);

  const loadData = useCallback((nextData: string) => {
    canvasDrawElementRef.current?.loadSaveData(nextData);
  }, []);

  const toggleDrawing = useCallback(() => {
    const snapshot = getData();
    if (snapshot) {
      setData(snapshot);
    }
    onTogglePainting();
  }, [getData, onTogglePainting]);

  useEffect(() => {
    if (!initialDrawing) {
      return;
    }
    loadData(initialDrawing);
  }, [initialDrawing, loadData]);

  useEffect(() => {
    if (!isCanvasReady || !canvasDrawElementRef.current) {
      return;
    }

    try {
      setData(getData());

      if (src) {
        canvasDrawElementRef.current.drawImage();
      }
    } catch (error) {
      logError('CanvasDraw drawImage failed', error);
      drawModeOff();
    }
  }, [windowSize, getData, src, isCanvasReady, drawModeOff]);

  useEffect(() => {
    if (!isCanvasReady || !canvasDrawElementRef.current || !data) {
      return;
    }

    try {
      loadData(data);
    } catch (error) {
      logError('CanvasDraw loadSaveData failed', error);
      drawModeOff();
    }
  }, [
    paintCanvasWidth,
    paintCanvasHeight,
    isCanvasReady,
    data,
    loadData,
    drawModeOff,
  ]);

  return {
    canvasDrawElementRef,
    setCanvasRef,
    stepBack,
    reset,
    getData,
    toggleDrawing,
  };
}
